import { useSyncExternalStore } from 'react'
import type { Language } from './i18n'
import { APP_VERSION } from './domain/appVersion'

export type AnalyticsEvent =
  | 'app_opened'
  | 'onboarding_completed'
  | 'area_created'
  | 'action_created'
  | 'action_started'
  | 'action_completed'
  | 'reward_created'
  | 'reward_redeemed'
  | 'feedback_opened'

const ENABLED_KEY = 'leveleria.analytics.enabled'
const SEEN_KEY = 'leveleria.analytics.seen'
const SOURCE_KEY = 'leveleria.analytics.source'
const listeners = new Set<() => void>()

const storedEnabled = () => {
  try {
    const stored = localStorage.getItem(ENABLED_KEY)
    if (stored !== null) return stored === 'true'
  } catch { /* Keep the privacy-first fallback below. */ }
  return navigator.doNotTrack !== '1'
}

let enabled = storedEnabled()

const source = () => {
  try {
    const querySource = new URLSearchParams(location.search).get('ref')?.toLocaleLowerCase().replace(/[^a-z0-9_-]/g, '').slice(0, 24)
    if (querySource) sessionStorage.setItem(SOURCE_KEY, querySource)
    return sessionStorage.getItem(SOURCE_KEY) ?? 'direct'
  } catch { return 'direct' }
}

const device = () => {
  const width = window.innerWidth
  if (width < 600) return 'mobile'
  if (width < 1024) return 'tablet'
  return 'desktop'
}

export const setAnalyticsEnabled = (next: boolean) => {
  enabled = next
  try { localStorage.setItem(ENABLED_KEY, String(next)) } catch { /* Keep the choice for this session. */ }
  listeners.forEach((listener) => listener())
}

export const useAnalyticsPreference = () => useSyncExternalStore(
  (listener) => { listeners.add(listener); return () => listeners.delete(listener) },
  () => enabled,
)

export const trackEvent = (event: AnalyticsEvent, language: Language) => {
  if (!enabled) return
  let returning = false
  try {
    returning = localStorage.getItem(SEEN_KEY) === 'true'
    localStorage.setItem(SEEN_KEY, 'true')
  } catch { /* Events do not require persistent storage. */ }
  void fetch('/api/events', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ event, language, device: device(), source: source(), version: APP_VERSION, returning }),
    keepalive: true,
  }).catch(() => { /* Analytics must never interrupt the app. */ })
}
